import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../context/StoreContext';
import { Icon } from '../shared/ui/Icon';
import { categoryName, safeImage } from '../entities/product/helpers';
import { ProductStrip } from '../widgets/ProductStrip';
import { CategoryIllustration } from '../entities/product/ProductImage';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const DEFAULT_HERO_INTERVAL = 6000;

function textValue(value) {
  return typeof value === 'object' && value ? value.en || '' : value || '';
}

function StatusMessage({ message, onRetry }) {
  return (
    <section className="resource-error" role="alert">
      <h2>We can’t load the storefront right now.</h2>
      <p>{message}</p>
      <button className="button button-primary" type="button" onClick={onRetry}>Try again</button>
    </section>
  );
}

export function HomePage() {
  const {
    homeCopy, brand, products, loading, settings, banners, categories, visibleCategories,
    resourceStatus, resourceErrors, retryCoreData, browseCategory, browseDeals, openProduct,
  } = useStore();
  const [activeIndex, setActiveIndex] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isDocumentVisible, setIsDocumentVisible] = useState(() => document.visibilityState !== 'hidden');
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia?.(REDUCED_MOTION_QUERY).matches || false);
  const pointerStart = useRef(null);
  const heroInterval = Math.max(1000, Number(settings?.heroInterval || settings?.carousel?.intervalMs) || DEFAULT_HERO_INTERVAL);

  const slides = useMemo(() => {
    if (banners.length) {
      return banners.map((banner) => ({
        id: banner.id,
        eyebrow: banner.eyebrow,
        line1: banner.titleLine1,
        line2: banner.titleLine2,
        subtitle: banner.subtitle,
        cta: banner.ctaLabel,
        href: banner.ctaUrl,
        image: banner.imageUrl,
        alt: banner.imageAlt || banner.titleLine1,
      }));
    }

    const discounted = products
      .filter((product) => Number(product.salePrice) > 0
        && Number(product.salePrice) < Number(product.price)
        && safeImage(product))
      .sort((a, b) => {
        const discountA = (Number(a.price) - Number(a.salePrice)) / Number(a.price);
        const discountB = (Number(b.price) - Number(b.salePrice)) / Number(b.price);
        return discountB - discountA;
      });
    const productSlides = discounted.slice(0, 3).map((product) => ({
      id: `product-${product.id}`,
      eyebrow: product.brand || product.categoryName || product.category || '',
      line1: product.title || product.name || '',
      line2: '',
      subtitle: product.description || '',
      cta: textValue(homeCopy.heroButtonText),
      href: product.slug || product.id,
      image: safeImage(product),
      alt: product.title || product.name || '',
    }));
    const categorySlides = categories
      .filter((category) => category.imageUrl)
      .slice(0, Math.max(0, 3 - productSlides.length))
      .map((category) => ({
        id: `category-${category.id || category.slug}`,
        eyebrow: category.name,
        line1: category.name,
        line2: '',
        subtitle: '',
        cta: textValue(homeCopy.heroButtonText),
        href: category.slug,
        image: category.imageUrl,
        alt: category.name,
      }));
    return [...productSlides, ...categorySlides];
  }, [banners, products, categories, homeCopy]);

  const isPaused = isHovered || isFocused || !isDocumentVisible;
  const activeSlideIndex = slides.length ? activeIndex % slides.length : 0;

  useEffect(() => {
    const media = window.matchMedia?.(REDUCED_MOTION_QUERY);
    const updateMotionPreference = (event) => setReducedMotion(event.matches);
    const updateDocumentVisibility = () => setIsDocumentVisible(document.visibilityState !== 'hidden');
    media?.addEventListener?.('change', updateMotionPreference);
    document.addEventListener('visibilitychange', updateDocumentVisibility);
    return () => {
      media?.removeEventListener?.('change', updateMotionPreference);
      document.removeEventListener('visibilitychange', updateDocumentVisibility);
    };
  }, []);

  useEffect(() => {
    if (activeIndex >= slides.length) setActiveIndex(0);
  }, [activeIndex, slides.length]);

  useEffect(() => {
    if (slides.length < 2) return;
    const nextImage = slides[(activeSlideIndex + 1) % slides.length].image;
    if (!nextImage) return;
    const image = new Image();
    image.src = nextImage;
  }, [activeSlideIndex, slides]);

  function showSlide(index) {
    if (slides.length < 2) return;
    setActiveIndex((index + slides.length) % slides.length);
    setProgressKey((key) => key + 1);
  }

  function handleSlideAction(slide) {
    if (/^https?:\/\//i.test(slide.href || '')) {
      window.open(slide.href, '_blank', 'noopener,noreferrer');
      return;
    }
    const category = categories.find((item) => item.slug === slide.href);
    if (category) return browseCategory(category.slug);
    const product = products.find((item) => String(item.id) === String(slide.href) || item.slug === slide.href);
    if (product) return openProduct(product);
    return browseCategory();
  }

  function handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showSlide(activeSlideIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      showSlide(activeSlideIndex + 1);
    }
  }

  function handlePointerDown(event) {
    if (event.pointerType === 'mouse') return;
    pointerStart.current = { x: event.clientX, y: event.clientY };
  }

  function handlePointerUp(event) {
    if (!pointerStart.current) return;
    const deltaX = event.clientX - pointerStart.current.x;
    const deltaY = event.clientY - pointerStart.current.y;
    pointerStart.current = null;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
    showSlide(activeSlideIndex + (deltaX < 0 ? 1 : -1));
  }

  const featuredIds = settings?.homeSections?.featuredProductIds;
  const featuredProducts = Array.isArray(featuredIds) && featuredIds.length
    ? featuredIds.map((id) => products.find((product) => String(product.id) === String(id))).filter(Boolean)
    : products.slice(0, 8);
  const saleProducts = products
    .filter((product) => Number(product.salePrice) > 0 && Number(product.salePrice) < Number(product.price))
    .sort((a, b) => {
      const discountA = (Number(a.price) - Number(a.salePrice)) / Number(a.price);
      const discountB = (Number(b.price) - Number(b.salePrice)) / Number(b.price);
      return discountB - discountA;
    });
  const categoryTiles = categories
    .filter((category) => Number(category.count) > 0)
    .map((category) => {
      const representative = products.find((product) => (
        String(product.category || '').toLowerCase() === String(category.slug).toLowerCase()
        && safeImage(product)
      ));
      return {
        ...category,
        image: category.imageUrl || (representative ? safeImage(representative) : ''),
      };
    });

  if (!loading && resourceStatus.products === 'error' && resourceStatus.categories === 'error') {
    return <StatusMessage message={resourceErrors.products || resourceErrors.categories} onRetry={retryCoreData} />;
  }

  const isLoadingSlides = loading || resourceStatus.banners === 'loading';

  return (
    <>
      {isLoadingSlides ? (
        <section className="hero hero-skeleton" aria-label="Loading featured promotions">
          <div className="hero-copy"><i /><i /><i /></div><div className="hero-art" />
        </section>
      ) : slides.length ? (
        <section
          className={`hero ${reducedMotion ? 'hero-reduced-motion' : ''} ${isPaused ? 'is-paused' : ''}`}
          style={{ '--hero-interval': `${heroInterval}ms` }}
          aria-roledescription="carousel"
          aria-label="Featured promotions"
          tabIndex={0}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setIsFocused(false);
          }}
          onKeyDown={handleKeyDown}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={() => { pointerStart.current = null; }}
        >
          <nav className="hero-categories" aria-label="Shop categories">
            <span className="eyebrow">SHOP CATEGORIES</span>
            {visibleCategories.map((category) => (
              <button key={category.slug} onClick={() => browseCategory(category.slug)}>
                <span>{categoryName(category)}</span><Icon>›</Icon>
              </button>
            ))}
            <button className="hero-all-categories" onClick={() => browseCategory()}>
              <span>All categories</span><Icon>↗</Icon>
            </button>
          </nav>
          <div className="hero-slides" aria-live="off">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className={`hero-slide ${index === activeSlideIndex ? 'is-active' : ''}`}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${slides.length}`}
                aria-hidden={index !== activeSlideIndex}
                inert={index !== activeSlideIndex}
              >
                <div className="hero-copy">
                  {!!slide.eyebrow && <span className="hero-kicker"><i /> {slide.eyebrow}</span>}
                  <h1>{slide.line1} {slide.line2 && <em>{slide.line2}</em>}</h1>
                  {!!slide.subtitle && <p>{slide.subtitle}</p>}
                  {!!slide.cta && (
                    <button className="button button-primary" onClick={() => handleSlideAction(slide)}>
                      {slide.cta}<Icon>↗</Icon>
                    </button>
                  )}
                </div>
                <div className="hero-art">
                  {slide.image && <img className="hero-image" src={slide.image} alt={slide.alt} width="900" height="600" fetchPriority={index === activeSlideIndex ? 'high' : 'auto'} />}
                </div>
              </div>
            ))}
          </div>
          {slides.length > 1 && (
            <div className="hero-controls" role="group" aria-label="Carousel controls">
              <div className="hero-dots">
                {slides.map((slide, index) => (
                  <button
                    key={slide.id}
                    type="button"
                    className={`hero-dot ${index === activeSlideIndex ? 'is-active' : ''}`}
                    aria-label={`Go to slide ${index + 1}`}
                    aria-current={index === activeSlideIndex ? 'true' : undefined}
                    onClick={() => showSlide(index)}
                  >
                    {index === activeSlideIndex && !reducedMotion && (
                      <span
                        key={`${activeSlideIndex}-${progressKey}`}
                        className="hero-progress-fill"
                        aria-hidden="true"
                        onAnimationEnd={() => {
                          if (!isPaused) {
                            setActiveIndex((current) => (current + 1) % slides.length);
                            setProgressKey((key) => key + 1);
                          }
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>
              <div className="hero-arrow-controls">
                <span>{String(activeSlideIndex + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}</span>
                <button type="button" aria-label="Previous slide" onClick={() => showSlide(activeSlideIndex - 1)}><Icon>‹</Icon></button>
                <button type="button" aria-label="Next slide" onClick={() => showSlide(activeSlideIndex + 1)}><Icon>›</Icon></button>
              </div>
            </div>
          )}
        </section>
      ) : (
        <section className="hero hero-static">
          <div className="hero-copy">
            <h1>{textValue(homeCopy.heroTitle) || brand}</h1>
            {!!textValue(homeCopy.heroSubtitle) && <p>{textValue(homeCopy.heroSubtitle)}</p>}
            {!!textValue(homeCopy.heroButtonText) && (
              <button className="button button-primary" onClick={() => browseCategory()}>
                {textValue(homeCopy.heroButtonText)}<Icon>↗</Icon>
              </button>
            )}
          </div>
        </section>
      )}

      {Array.isArray(settings?.trustItems) && settings.trustItems.length > 0 && (
        <div className="benefit-row">
          {settings.trustItems.map((item) => (
            <div key={item.id || item.title}>
              <span className="benefit-icon">{item.icon ? <Icon>{item.icon}</Icon> : null}</span>
              <div><b>{item.title}</b>{item.description && <small>{item.description}</small>}</div>
            </div>
          ))}
        </div>
      )}

      {categoryTiles.length > 0 && (
        <section className="category-section">
          <div className="section-heading">
            <div><p className="eyebrow">FIND YOUR THING</p><h2>{textValue(homeCopy.categoriesTitle) || 'Shop by category'}</h2></div>
            <button className="text-link" onClick={() => browseCategory()}>All categories <Icon>↗</Icon></button>
          </div>
          <div className="category-grid">
            {categoryTiles.map((category, index) => (
              <button
                className={`category-tile ${category.image ? '' : 'category-tile-placeholder'}`}
                key={category.slug}
                onClick={() => browseCategory(category.slug)}
              >
                {category.image
                  ? <img src={category.image} alt="" loading="lazy" />
                  : <span className="category-placeholder-art" aria-hidden="true"><CategoryIllustration category={category.slug || category.name} /></span>}
                <span className="category-number">{String(index + 1).padStart(2, '0')}</span>
                <span className="category-name">{categoryName(category)}</span>
                <span className="category-arrow"><Icon>↗</Icon></span>
              </button>
            ))}
          </div>
        </section>
      )}

      <ProductStrip
        items={saleProducts}
        title={textValue(homeCopy.onSaleTitle)}
        subtitle={textValue(homeCopy.onSaleSubtitle)}
        action={saleProducts.length ? browseDeals : undefined}
        loading={loading}
      />
      <ProductStrip
        items={featuredProducts}
        title={textValue(homeCopy.featuredTitle)}
        action={featuredProducts.length ? () => browseCategory() : undefined}
        loading={loading}
      />
      {!loading && products.length === 0 && (
        <section className="empty-state">
          <h2>No products are available yet.</h2>
          <p>The store catalog is currently empty.</p>
        </section>
      )}
    </>
  );
}
